"use client";

import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type {
  CSSProperties,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
} from "react";
import {
  CV_SCREEN_RHYTHM,
  LAYOUT_MAP,
  getAccentHex,
  getLayoutTokens,
} from "@tabbio/tokens/cv-layout";
import { CvContactIcon, type CvContactIconId } from "./cv-contact-icon";
import { resolveUaePassMark, resolveUaePassMarkOpacity } from "./verification";
import type { CVLayoutStyle, CvSectionId } from "@tabbio/types";
import { normalizeCvProfileContent } from "@tabbio/utils/cv-content-normalization";
import { getVisibleCvSections } from "@tabbio/utils/cv-section-config";
import {
  containsArabicScript,
  detectCvContentDirection,
} from "@tabbio/utils/cv-content-direction";
import { resolveBidiTextDirection } from "@tabbio/utils/bidi-text";
import { splitEducationDescription } from "@tabbio/utils/education-description";
import { resolveCvLink, type CvLinkKey } from "@tabbio/utils/cv-profile-links";
import { localizeCvLanguageEntry } from "@tabbio/utils/cv-language-proficiency";
export {
  DEFAULT_CV_SECTION_ORDER,
  normalizeCvSectionOrder as resolveCvSectionOrder,
} from "@tabbio/utils/cv-section-config";

export type WebCvConfig = {
  style: CVLayoutStyle;
  color: string;
  showPhoto: boolean;
  /**
   * Best-effort single-page mode (CVConfigData.fitToOnePage, off by default):
   * the sheet content scales down to the one-page height, mirroring what the
   * PDF export does with its print tokens. Ignored below the 720px breakpoint
   * where the sheet has no page metaphor.
   */
  fitToOnePage?: boolean;
  /**
   * `CVConfigData.hideTabbioLink` (Pro: individual.custom_branding). When true
   * the canonical `{handle}.tabbio.com` row is dropped from the contact header
   * on every viewer mode, including a `tabbio` entry a caller supplied through
   * `contacts` - the owner asked for the link off, and a reader-facing share
   * must not re-add it.
   */
  hideTabbioLink?: boolean;
};

export type WebCvContact = {
  /**
   * The contact vocabulary, declared beside the icons that draw it
   * (`cv-contact-icon.tsx`) so the two cannot drift: a new id added here
   * without a mark would silently fall through to the globe.
   */
  id: CvContactIconId;
  label: string;
  href?: string | null;
  kind?: "fact" | "link" | "channel";
};

export type WebCvProfile = {
  /** Persisted edition direction, independent of the viewer interface. */
  documentDirection?: "ltr" | "rtl";
  name: string;
  username: string;
  /**
   * Pre-resolved public WebCV URL, or null when `{handle}.tabbio.com` would
   * 404. Reader-facing views (`viewerMode` `public`/`private`) print the
   * canonical Tabbio link only when this is set; see
   * `resolveCanonicalTabbioLink`.
   */
  publicCvUrl?: string | null;
  headline?: string | null;
  location?: string | null;
  avatarUrl?: string | null;
  contactEmail?: string | null;
  phone?: string | null;
  website?: string | null;
  linkedin?: string | null;
  github?: string | null;
  dribbble?: string | null;
  behance?: string | null;
  summary?: string | null;
  highlights: Array<{ id?: string; title: string; desc: string }>;
  skillGroups: Array<{ category: string; items: string[] }>;
  experience: Array<{
    id: string;
    role: string;
    company: string;
    location?: string | null;
    locationType?: string | null;
    employmentType?: string | null;
    period?: string | null;
    overview?: string | null;
    bullets: string[];
  }>;
  education: Array<{
    id: string;
    degree: string;
    school: string;
    year?: string | null;
    description?: string | null;
  }>;
  projects?: Array<{
    id: string;
    title: string;
    description?: string | null;
    link?: string | null;
    technologies: string[];
  }>;
  certifications: Array<{
    id: string;
    name: string;
    issuer?: string | null;
    date?: string | null;
    credentialUrl?: string | null;
  }>;
  languages: string[];
};

export type WebCvLabels = {
  profileSummary: string;
  careerHighlights: string;
  keySkills: string;
  professionalExperience: string;
  education: string;
  projects: string;
  certifications: string;
  languages: string;
  verifyCredential: string;
};

export type WebCvEditSection =
  | "edit-header"
  | "edit-summary"
  | "edit-highlights"
  | "edit-skills"
  | "edit-experience"
  | "edit-education"
  | "edit-projects"
  | "edit-certifications"
  | "edit-languages";

export type WebCvRendererProps = {
  readOnlyProjects?: boolean;
  profile: WebCvProfile;
  config: WebCvConfig;
  labels?: Partial<WebCvLabels>;
  contacts?: WebCvContact[];
  /**
   * Host controls rendered above the document canvas. This slot intentionally
   * stays outside the light document theme boundary.
   */
  topSlot?: ReactNode;
  /**
   * Authenticated editor guidance rendered inside the document, after its
   * header and before the first content section. Omitted by public/export
   * callers so it never becomes part of a shared or downloaded CV.
   */
  bodyLeadSlot?: ReactNode;
  maxWidth?: number;
  className?: string;
  style?: CSSProperties;
  /**
   * `light` preserves the canonical public/export document palette.
   * `adaptive` lets authenticated app previews inherit the host web theme.
   */
  appearance?: "light" | "adaptive";
  /**
   * Optional edit hook used by authenticated app surfaces. Public CV embeds stay
   * read-only when this prop is omitted.
   */
  onEditSection?: (section: WebCvEditSection) => void;
  /**
   * Optional avatar-specific edit hook. Authenticated onboarding uses this to
   * edit identity/photo while the rest of the header edits contact details.
   */
  onEditPhoto?: () => void;
  /** Accessible label for the avatar-specific edit control. */
  editPhotoLabel?: string;
  /**
   * When false, the header (avatar, name, headline, contact list) is skipped
   * and the document starts directly at the profile summary section. Useful
   * for embedding the CV inside screens that already show the candidate's
   * identity above the preview (e.g. employer candidate detail).
   * Defaults to true.
   */
  showHeader?: boolean;
  /**
   * Right-to-left layout. When true, all text elements (which use `dir="auto"`
   * for correct mixed-script bidi) are explicitly right-aligned so Latin-only
   * fields do not stay left-aligned inside an RTL document.
   */
  isRTL?: boolean;
  /**
   * Section include-list + ordering, mirroring
   * `TailoredCVOverrides.sectionsConfig` (skills two-way design). When
   * `order` is present, exactly those sections render in that order -
   * a section id omitted from the list is hidden. When absent/null, the
   * legacy hardcoded order applies (zero behavior change for existing CVs).
   * The header (name/title/contact) always renders first regardless, and
   * content-empty sections are still skipped.
   */
  sectionsConfig?: { order: CvSectionId[] } | null;
  /** Controls which contact details are allowed to render. */
  viewerMode?: "self" | "public" | "private";
  /**
   * Render only from an authoritative verification value supplied by the caller.
   * The mark is monochrome - it takes the name's own color either way. When
   * `isVerified` is true it shows at full strength; when false, the same mark is
   * dimmed. Both states render on reader-facing views too (decision 5 Aug 2026):
   * a reader is shown the affordance either way.
   */
  verification?: { isVerified: boolean; label?: string } | null;
  /**
   * Tap handler for the verification mark. When provided, the mark becomes a
   * button that opens the caller's UAE PASS dialog. The owner's own view opens
   * the app's verification sheet; a shared/public CV opens the reader-facing
   * dialog (verified + date, or "not verified"). Omit it to leave the mark
   * decorative.
   */
  onVerifyPress?: () => void;
};

export const DEFAULT_WEB_CV_LABELS: WebCvLabels = {
  profileSummary: "Profile summary",
  careerHighlights: "Career highlights",
  keySkills: "Key skills",
  professionalExperience: "Professional experience",
  education: "Education",
  projects: "Projects",
  certifications: "Certifications",
  languages: "Languages",
  verifyCredential: "Verify",
};

export const AR_WEB_CV_LABELS: WebCvLabels = {
  profileSummary: "الملخص المهني",
  careerHighlights: "أبرز الإنجازات",
  keySkills: "المهارات الرئيسية",
  professionalExperience: "الخبرة المهنية",
  education: "التعليم",
  projects: "المشاريع",
  certifications: "الشهادات",
  languages: "اللغات",
  verifyCredential: "عرض الشهادة",
};

const ARABIC_LETTER_RE =
  /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g;
const LATIN_LETTER_RE = /[A-Za-z]/g;

// Non-global clones so `search()` can be used without touching `lastIndex`.
const FIRST_ARABIC_LETTER_RE = new RegExp(ARABIC_LETTER_RE.source);
const FIRST_LATIN_LETTER_RE = new RegExp(LATIN_LETTER_RE.source);

/**
 * Resolve a single string's direction the way `dir="auto"` would — from its
 * first strong directional character. Used for flex rows that need to flow from
 * the same edge as the text inside them. `dir="auto"` on the row would rely on
 * the HTML auto-direction algorithm reaching through a child that carries its
 * own `dir` attribute, which is engine-dependent; resolving from the one string
 * that matters is deterministic. Returns undefined when there is no strong
 * character, leaving the row to inherit the document direction.
 *
 * That last case deliberately differs from `dir="auto"`: per the HTML spec
 * `dir="auto"` falls back to LTR when it finds no strong character (verified in
 * Chromium and WebKit), it does NOT inherit. For a digits-only row - a phone
 * number, a date range - inheriting is what we want, because the surrounding
 * document direction is the only signal available.
 */
function resolveTextDir(value?: string | null): "rtl" | "ltr" | undefined {
  if (!value) return undefined;
  const arabicAt = value.search(FIRST_ARABIC_LETTER_RE);
  const latinAt = value.search(FIRST_LATIN_LETTER_RE);
  if (arabicAt < 0 && latinAt < 0) return undefined;
  if (arabicAt < 0) return "ltr";
  if (latinAt < 0) return "rtl";
  return arabicAt < latinAt ? "rtl" : "ltr";
}

function BidiInlineList({
  values,
  separator,
  baseDirection,
  style,
}: {
  values: ReadonlyArray<string | null | undefined>;
  separator: string;
  baseDirection: "ltr" | "rtl";
  style?: CSSProperties;
}) {
  const parts = values
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value));
  if (parts.length === 0) return null;
  const flexDirection = baseDirection === "rtl" ? "row-reverse" : "row";
  const tightSeparator = /^[,.;:!?،؛]+$/u.test(separator.trim());

  // Punctuation belongs to the preceding item. A flex group containing the
  // separator and next skill produced leading commas after every wrapped row.
  // Inline bidi isolates also let long skill labels wrap at their own spaces.
  if (tightSeparator) {
    return (
      <span
        dir={baseDirection}
        style={{ ...style, display: "block", overflowWrap: "anywhere" }}
      >
        {parts.map((part, index) => (
          <Fragment key={`${index}-${part}`}>
            <bdi dir={resolveBidiTextDirection(part, baseDirection)}>
              {part}
              {index < parts.length - 1 && (
                <span aria-hidden="true">{separator}</span>
              )}
            </bdi>
            {index < parts.length - 1 ? " " : null}
          </Fragment>
        ))}
      </span>
    );
  }

  const valueNode = (part: string, index: number) => (
    <bdi
      key={`value-${index}-${part}`}
      dir={resolveBidiTextDirection(part, baseDirection)}
      style={{ unicodeBidi: "isolate" }}
    >
      {part}
    </bdi>
  );

  return (
    <span
      dir={baseDirection}
      style={{
        ...style,
        display: "flex",
        flexDirection,
        flexWrap: "wrap",
        alignItems: "baseline",
        minWidth: 0,
      }}
    >
      {valueNode(parts[0], 0)}
      {parts.slice(1).map((part, index) => (
        <span
          key={`group-${index + 1}-${part}`}
          style={{
            display: "inline-flex",
            flexDirection,
            alignItems: "baseline",
            whiteSpace: "nowrap",
          }}
        >
          <span aria-hidden="true" style={{ marginInline: 5 }}>
            {separator}
          </span>
          {valueNode(part, index + 1)}
        </span>
      ))}
    </span>
  );
}

/** Infer RTL for public/private CVs where no app locale is available. */
export function detectWebCvRtl(profile: WebCvProfile): boolean {
  return detectCvContentDirection(profile) === "rtl";
}

/** The stored level renders from its canonical code in the document language. */
function localizeWebCvLanguage(entry: string, isRtl: boolean): string {
  return localizeCvLanguageEntry(entry, isRtl ? "ar" : "en");
}

// Public CVs remain light documents, while authenticated app previews can opt
// into an adaptive palette. Keep renderer variables namespaced so neither mode
// leaks into its host surface.
const TEXT = "var(--tabbio-cv-text, #0d0d0d)";
const TEXT_SECONDARY = "var(--tabbio-cv-text-secondary, #5d5d5d)";
const TEXT_TERTIARY = "var(--tabbio-cv-text-tertiary, #8f8f8f)";
const BORDER = "var(--tabbio-cv-border, rgba(0,0,0,0.10))";
const SURFACE = "var(--tabbio-cv-surface, #f9f9f9)";
const ADAPTIVE_BLACK_SECTION_HEADING =
  "var(--tabbio-cv-black-section-heading, #1D1D1F)";
const ADAPTIVE_BLACK_SECTION_ACCENT =
  "var(--tabbio-cv-black-section-accent, #1D1D1F)";
// A BLACK accent is chosen against white paper, so in the adaptive palette it
// collapses into the dark sheet: accent ink (skill pills, accent bullets, the
// CLASSIC name, contact icons) and the 9%-accent tint behind skill pills both
// disappear. These follow the app's own text color, exactly as the section
// heading/rule remap above does.
const ADAPTIVE_BLACK_ACCENT_INK = "var(--tabbio-cv-black-accent-ink, #0A0A0A)";
const ADAPTIVE_BLACK_ACCENT_SOFT =
  "var(--tabbio-cv-black-accent-soft, rgba(10, 10, 10, 0.09))";
const EDITABLE_REGION_CLASS = "tabbio-web-cv-editable-region";
const SHELL_CLASS = "tabbio-web-cv-shell";
const DOCUMENT_CLASS = "tabbio-web-cv-document";
const ADAPTIVE_DOCUMENT_CLASS = "tabbio-web-cv-document-adaptive";
const CREATIVE_HEADER_CLASS = "tabbio-web-cv-creative-header";
const EDITABLE_REGION_STYLES = `
.${EDITABLE_REGION_CLASS} {
  margin-inline: -10px;
  padding: 6px 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: background-color 150ms cubic-bezier(0.19, 1, 0.22, 1);
}
.${EDITABLE_REGION_CLASS}:hover,
.${EDITABLE_REGION_CLASS}:focus-visible {
  background-color: var(--tabbio-cv-editable-hover, rgba(13, 13, 13, 0.04));
}
`;
const STYLE_CLASS_PREFIX = "tabbio-web-cv-style-";
// The on-screen sheet mirrors each template's true A4 print margin so density
// and line breaks track the exported PDF: an 850px sheet stands in for the
// 210mm page, so 1mm ≙ 850/210 px. Block padding sits slightly tighter than
// the inline padding, matching classic resume margins.
const SHEET_PADDING = (
  Object.keys(LAYOUT_MAP) as Array<keyof typeof LAYOUT_MAP>
).map((styleKey) => {
  const marginMm = LAYOUT_MAP[styleKey].print.pageMarginMm;
  const inlinePx = Math.round((marginMm / 210) * 850);
  return { styleKey, marginMm, inlinePx, blockPx: inlinePx - 8 };
});
const DOCUMENT_STYLES = `
.${SHELL_CLASS} {
  width: 100%;
  margin: 0 auto;
}
.${DOCUMENT_CLASS} {
  /*
   * Public/share/print CVs use the canonical light document contract.
   * Authenticated previews override only these namespaced tokens below.
   */
  color-scheme: light;
  --tabbio-cv-text: #0d0d0d;
  --tabbio-cv-text-secondary: #5d5d5d;
  --tabbio-cv-text-tertiary: #8f8f8f;
  --tabbio-cv-surface: #f9f9f9;
  --tabbio-cv-black-section-heading: #1D1D1F;
  --tabbio-cv-black-section-accent: #1D1D1F;
  --tabbio-cv-black-accent-ink: #0A0A0A;
  --tabbio-cv-black-accent-soft: rgba(10, 10, 10, 0.09);
  --tabbio-cv-editable-hover: rgba(13, 13, 13, 0.04);
  --cv-inline-padding: 50px;
  --cv-block-padding: 53px;
  box-sizing: border-box;
  min-height: 1202px;
  padding: var(--cv-block-padding) var(--cv-inline-padding) calc(var(--cv-block-padding) + 8px);
  border-radius: 14px;
  background: #fff;
  box-shadow: 0px 10px 30px rgba(0,0,0,0.05);
  overflow: hidden;
}
${SHEET_PADDING.map(
  ({ styleKey, inlinePx, blockPx }) => `@media (min-width: 720px) {
  .${STYLE_CLASS_PREFIX}${styleKey} {
    --cv-inline-padding: ${inlinePx}px;
    --cv-block-padding: ${blockPx}px;
  }
}`,
).join("\n")}
.${DOCUMENT_CLASS}.${ADAPTIVE_DOCUMENT_CLASS} {
  color-scheme: normal;
  --tabbio-cv-text: var(--text-primary, #0d0d0d);
  --tabbio-cv-text-secondary: var(--text-secondary, #5d5d5d);
  --tabbio-cv-text-tertiary: var(--text-tertiary, #8f8f8f);
  --tabbio-cv-border: var(--input-border, rgba(0, 0, 0, 0.10));
  --tabbio-cv-surface: var(--hover, #f9f9f9);
  --tabbio-cv-black-section-heading: var(--text-primary, #0d0d0d);
  --tabbio-cv-black-section-accent: var(--text-secondary, #5d5d5d);
  --tabbio-cv-black-accent-ink: var(--text-primary, #0d0d0d);
  --tabbio-cv-black-accent-soft: color-mix(in srgb, var(--text-primary, #0d0d0d) 12%, transparent);
  --tabbio-cv-editable-hover: color-mix(in srgb, var(--text-primary, #0d0d0d) 6%, transparent);
  background: var(--menu-bg, #fff);
  box-shadow: var(--gem-input-shadow, 0 16px 45px rgba(10, 10, 10, 0.10));
}
.${CREATIVE_HEADER_CLASS} {
  /*
   * The band escapes the document padding via negative margins. Those live in
   * the fit-scaled coordinate space when fit-to-one-page is active, so divide
   * by the scale to keep the band full-bleed (--cv-fit-scale is 1 otherwise).
   */
  margin-top: calc(var(--cv-block-padding) / var(--cv-fit-scale, 1) * -1);
  margin-inline: calc(var(--cv-inline-padding) / var(--cv-fit-scale, 1) * -1);
  padding: 36px calc(var(--cv-inline-padding) / var(--cv-fit-scale, 1)) 30px;
}
.${EDITABLE_REGION_CLASS}.${CREATIVE_HEADER_CLASS} {
  margin-top: calc(var(--cv-block-padding) / var(--cv-fit-scale, 1) * -1);
  margin-inline: calc(var(--cv-inline-padding) / var(--cv-fit-scale, 1) * -1);
  padding: 36px calc(var(--cv-inline-padding) / var(--cv-fit-scale, 1)) 30px;
  border-radius: 0;
}
.tabbio-cv-contact-menu > summary {
  list-style: none;
}
.tabbio-cv-contact-menu > summary::-webkit-details-marker {
  display: none;
}
@media (max-width: 719px) {
  .${DOCUMENT_CLASS} {
    --cv-inline-padding: 6px;
    --cv-block-padding: 28px;
    min-height: 0;
    border-radius: 0;
    box-shadow: none;
  }
  .${CREATIVE_HEADER_CLASS} {
    padding-top: 28px;
    padding-bottom: 24px;
  }
  .${EDITABLE_REGION_CLASS}.${CREATIVE_HEADER_CLASS} {
    padding-top: 28px;
    padding-bottom: 24px;
  }
}
@media print {
  .${SHELL_CLASS} {
    max-width: none !important;
  }
  .${DOCUMENT_CLASS} {
    width: 100% !important;
    max-width: none !important;
    min-height: 0;
    margin: 0;
    border-radius: 0;
    box-shadow: none;
    overflow: visible;
  }
${SHEET_PADDING.map(
  ({ styleKey, marginMm }) => `  .${STYLE_CLASS_PREFIX}${styleKey} {
    --cv-inline-padding: ${marginMm}mm;
    --cv-block-padding: ${marginMm - 3}mm;
  }`,
).join("\n")}
  .${DOCUMENT_CLASS}.${ADAPTIVE_DOCUMENT_CLASS} {
    color-scheme: light;
    --tabbio-cv-text: #0d0d0d;
    --tabbio-cv-text-secondary: #5d5d5d;
    --tabbio-cv-text-tertiary: #8f8f8f;
    --tabbio-cv-border: rgba(0, 0, 0, 0.10);
    --tabbio-cv-surface: #f9f9f9;
    --tabbio-cv-black-section-heading: #1D1D1F;
    --tabbio-cv-black-section-accent: #1D1D1F;
    --tabbio-cv-black-accent-ink: #0A0A0A;
    --tabbio-cv-black-accent-soft: rgba(10, 10, 10, 0.09);
    background: #fff;
  }
}
`;

export function getInitials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function normalizePublicHandle(handle?: string | null) {
  return typeof handle === "string"
    ? handle.trim().replace(/^@+/, "").replace(/_/g, "-").toLowerCase()
    : "";
}

function buildPersonCvUrl(handle?: string | null) {
  const normalized = normalizePublicHandle(handle);
  // No handle -> no link. Falling back to the marketing site printed
  // `www.tabbio.com` on CVs whose profile had no username yet (the onboarding
  // preview), which reads as a broken link — the row is omitted instead,
  // matching the native preview's issue #241 contract.
  return normalized
    ? `https://${encodeURIComponent(normalized)}.tabbio.com`
    : null;
}

type TabbioCvLink = { label: string; href: string };

function toTabbioCvLink(href: string): TabbioCvLink {
  return {
    label: href.replace(/^https?:\/\//, "").replace(/\/$/, ""),
    href,
  };
}

function getTabbioCvLink(username?: string | null): TabbioCvLink | null {
  const href = buildPersonCvUrl(username);
  return href ? toTabbioCvLink(href) : null;
}

/**
 * The canonical WebCV link to print, or null when it would not resolve.
 *
 * `publicCvUrl` is the server's pre-resolved answer (null whenever
 * `{handle}.tabbio.com` would 404 — unpublished, private, or not
 * discoverable). Deriving the link from the handle instead is only safe on the
 * owner's own preview: a reader-facing CV cannot be recalled, so advertising a
 * dead link there is worse than omitting the row (issues #240, #241, #281).
 * Share links resolve by token regardless of profile visibility, so `private`
 * shares reach outside readers too and must be gated the same as `public`.
 */
function resolveCanonicalTabbioLink(
  profile: WebCvProfile,
  viewerMode: "self" | "public" | "private",
  hidden: boolean,
): TabbioCvLink | null {
  if (hidden) return null;
  const publicCvUrl = profile.publicCvUrl?.trim();
  if (publicCvUrl) return toTabbioCvLink(publicCvUrl);
  return viewerMode === "self" ? getTabbioCvLink(profile.username) : null;
}

function defaultContacts(
  profile: WebCvProfile,
  tabbioLink: TabbioCvLink | null,
): WebCvContact[] {
  const profileLinkContact = (
    id: CvLinkKey,
    value?: string | null,
  ): WebCvContact | null => {
    const link = resolveCvLink(id, value);
    return link ? { id, ...link } : null;
  };

  return [
    profile.location ? { id: "location", label: profile.location } : null,
    profile.phone
      ? { id: "phone", label: profile.phone, href: `tel:${profile.phone}` }
      : null,
    profile.contactEmail
      ? {
          id: "email",
          label: profile.contactEmail,
          href: `mailto:${profile.contactEmail}`,
        }
      : null,
    profileLinkContact("website", profile.website),
    profileLinkContact("linkedin", profile.linkedin),
    profileLinkContact("github", profile.github),
    profileLinkContact("dribbble", profile.dribbble),
    profileLinkContact("behance", profile.behance),
    tabbioLink
      ? { id: "tabbio", label: tabbioLink.label, href: tabbioLink.href }
      : null,
  ].filter((contact): contact is WebCvContact => Boolean(contact));
}

/**
 * Exported so marketing surfaces can show the same mark a real CV shows, rather
 * than a generic tick or a fourth copy of this path. Callers outside the CV pass
 * the ink color they sit on and `resolveUaePassMarkOpacity(variant)` for the
 * verified/dimmed split.
 */
export function UaePassVerifiedMark({
  label = "UAE PASS verified",
  color = "#373737",
  opacity = 1,
  size = 14,
  marginInlineStart = 7,
}: {
  label?: string;
  color?: string;
  opacity?: number;
  /** Square edge, in px. The CV header keeps the default; dialogs scale it up. */
  size?: number;
  /**
   * Leading gap from the word before it. Defaults to the CV header's spacing
   * because that is the mark's home; a standalone use (a dialog hero) passes 0
   * rather than cancelling the margin with a negative one further out.
   */
  marginInlineStart?: number;
}) {
  // `aria-label` on a bare <span> is prohibited ARIA - a generic element cannot
  // take an accessible name - so the mark declares `role="img"`, which is what it
  // actually is: a graphic with a text alternative. Callers that pass `label=""`
  // (the dialog hero, which labels itself) get a decorative mark instead, rather
  // than an image role with no name.
  const decorative = label.trim().length === 0;
  return (
    <span
      title={decorative ? undefined : label}
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : label}
      aria-hidden={decorative ? true : undefined}
      style={{
        display: "inline-flex",
        alignItems: "center",
        flexShrink: 0,
        // The mark sits in the name's own text flow, so it needs baseline
        // alignment and logical (RTL-safe) spacing rather than a flex `gap`.
        verticalAlign: "middle",
        marginInlineStart,
        opacity,
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 14 14"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M6.93784 0.778334C7.11827 0.758795 8.00001 1.12873 8.22593 1.21583L10.099 1.93068L11.5511 2.48244C11.7971 2.57647 12.2011 2.71933 12.4203 2.84279C12.4686 3.42727 12.4261 4.28299 12.4339 4.88673C12.4589 6.81736 12.4876 8.27014 11.5433 10.0313C10.7309 11.5463 9.573 12.5779 8.05893 13.3838C7.75225 13.5471 7.37917 13.7393 7.05112 13.8408C6.76872 13.8489 5.92117 13.3535 5.64292 13.1992C4.0887 12.3375 2.94889 11.1017 2.20932 9.49025C1.40769 7.74357 1.57019 6.05793 1.56479 4.19728C1.58145 3.84567 1.5211 3.21844 1.59214 2.8965C1.62272 2.76267 2.90568 2.3305 3.04429 2.27443C4.32743 1.755 5.67048 1.32515 6.93784 0.778334ZM7.01987 1.959C6.73796 2.02864 6.17655 2.27116 5.88706 2.38478L3.85483 3.16505C3.56078 3.27807 3.03965 3.49734 2.76792 3.58009V6.33595C2.69833 9.19407 4.46452 11.4578 7.01694 12.5498C8.76239 11.7811 10.1199 10.5836 10.8177 8.77247C11.252 7.64522 11.2543 6.74267 11.2503 5.56154C11.2482 4.91649 11.2421 4.22292 11.2503 3.57814C10.9339 3.48093 10.4421 3.26934 10.1166 3.14259L7.94272 2.29689C7.80088 2.24148 7.11719 1.95865 7.01987 1.959ZM7.14194 5.4756C7.56282 5.51248 7.9454 5.75035 8.21421 6.06642C8.86445 6.8313 8.44672 7.58389 8.73764 8.42872C8.76673 8.51308 8.83383 8.67181 8.88218 8.75001C8.98725 8.91979 9.26238 9.12867 9.24057 9.34474C9.22386 9.50829 9.00741 9.6594 8.84604 9.63185C8.68953 9.60475 8.5508 9.47507 8.45542 9.35744C8.0851 8.90047 7.9762 8.27653 7.95932 7.70412C7.94569 7.46756 7.97808 7.20567 7.93686 6.97267C7.85545 6.5127 7.47499 6.16943 7.01792 6.11134C6.63482 6.06278 6.3563 6.19483 6.06479 6.41701C6.01108 6.46653 5.96297 6.52239 5.92221 6.58302C5.83615 6.71164 5.77583 6.85628 5.74448 7.00783C5.70375 7.21338 5.71963 7.43854 5.71714 7.64747C5.70886 8.33442 5.7583 9.00057 5.96616 9.66017C6.03869 9.88989 6.26191 10.1749 6.31089 10.4199C6.32754 10.5048 6.24061 10.6103 6.19077 10.6817C6.13195 10.7117 6.02681 10.7459 5.96128 10.7695C5.89065 10.7528 5.81598 10.7268 5.74643 10.7041C5.32864 10.1907 5.12174 9.26149 5.08042 8.6133C5.06271 8.31947 5.05538 8.02485 5.05698 7.73048C5.05445 7.09065 5.06281 6.55601 5.51889 6.04689C5.90985 5.61079 6.31454 5.49066 6.87241 5.45802C6.96225 5.46234 7.05227 5.46837 7.14194 5.4756ZM6.76304 6.79689C7.54158 6.72767 7.15044 7.87121 7.21909 8.35255C7.32475 9.09297 7.51014 9.53657 7.96616 10.127C8.13586 10.3467 7.87669 10.5482 7.70639 10.6406C7.28524 10.599 6.9221 9.90801 6.77182 9.56056C6.66797 9.32222 6.58731 9.07403 6.53061 8.82033C6.43959 8.41071 6.34245 7.49017 6.45054 7.04201C6.47968 6.92215 6.65242 6.84075 6.76304 6.79689ZM6.83921 3.97365C8.4698 3.88133 9.91353 5.27791 9.95347 6.89455C9.96067 7.1851 10.0286 7.79101 9.84995 8.02931C9.74994 8.16207 9.41474 8.10721 9.35971 7.94044C9.31377 7.80077 9.32563 7.58389 9.31968 7.43556C9.3543 6.65292 9.19415 5.9177 8.62241 5.34767C8.19331 4.90207 7.55205 4.63767 6.92807 4.62794C6.38832 4.62845 5.66676 4.92285 5.25913 5.28322C4.78463 5.70301 4.47736 6.26398 4.39878 6.8965C4.35235 7.18603 4.37361 7.47956 4.35679 7.76759C4.33229 8.18169 3.93598 8.20062 3.70932 7.89357C3.7135 7.42725 3.67934 7.09578 3.77768 6.6338C4.10434 5.10012 5.23436 4.14659 6.77085 3.97951C6.7933 3.9772 6.81668 3.97493 6.83921 3.97365Z"
          fill={color}
        />
      </svg>
    </span>
  );
}

/**
 * Gates and colors the UAE PASS mark for the CV header.
 * - The mark is monochrome and always takes the name's own color, so it reads
 *   as part of the name (LinkedIn's treatment) instead of a status chip.
 * - Verified -> full strength.
 * - Unverified -> that same color dimmed, on reader-facing views too.
 * - When `onVerifyPress` is supplied (owner), the mark is a button that opens
 *   the shared UAE PASS dialog.
 *
 * The accessible label follows the variant, not the caller's `label`: a dimmed
 * mark announced as "UAE PASS verified" would tell a screen reader the opposite
 * of what it means. Callers may only override the *verified* wording.
 */
function UaePassVerifiedControl({
  verification,
  color,
  onVerifyPress,
}: {
  verification: { isVerified: boolean; label?: string } | null | undefined;
  color: string;
  onVerifyPress?: () => void;
}) {
  const variant = resolveUaePassMark(verification);
  if (!variant) return null;
  const label =
    variant === "verified"
      ? (verification?.label ?? "UAE PASS verified")
      : "Not verified with UAE PASS";
  const mark = (
    <UaePassVerifiedMark
      label={label}
      color={color}
      opacity={resolveUaePassMarkOpacity(variant)}
    />
  );
  if (!onVerifyPress) return mark;
  return (
    <button
      type="button"
      onClick={onVerifyPress}
      aria-label={label}
      aria-haspopup="dialog"
      title={label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        flexShrink: 0,
        verticalAlign: "middle",
        padding: 0,
        margin: 0,
        border: "none",
        background: "transparent",
        cursor: "pointer",
        lineHeight: 0,
      }}
    >
      {mark}
    </button>
  );
}

function SectionHeader({
  title,
  accentColor,
  headingColor,
  tokens,
}: {
  title: string;
  accentColor: string;
  headingColor?: string;
  tokens: ReturnType<typeof getLayoutTokens>;
}) {
  if (tokens.sectionHeaderHasBar) {
    return (
      <div
        style={{
          marginTop: tokens.sectionGap,
          marginBottom: tokens.labelToContentGap,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              width: 4,
              height: 20,
              borderRadius: 2,
              backgroundColor: accentColor,
            }}
          />
          <h3
            style={{
              fontSize: tokens.sectionHeaderSize,
              fontWeight: tokens.sectionHeaderWeight,
              margin: 0,
              fontFamily: "inherit",
              color: headingColor ?? TEXT,
            }}
          >
            {title}
          </h3>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: tokens.sectionGap,
        marginBottom: tokens.labelToContentGap,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h3
          style={{
            fontSize: tokens.sectionHeaderSize,
            fontWeight: tokens.sectionHeaderWeight,
            letterSpacing:
              tokens.sectionHeaderRuleWidth === "sixty-percent" ? "0.08em" : 0,
            textTransform: "uppercase",
            margin: 0,
            fontFamily: "inherit",
            color:
              headingColor ??
              (tokens.sectionHeaderUsesAccent ? accentColor : TEXT),
            flexShrink: 0,
          }}
        >
          {title}
        </h3>
        {tokens.sectionHeaderRuleWidth === "sixty-percent" ? (
          <span
            style={{
              height: 1,
              backgroundColor: `${accentColor}99`,
              flex: "0 1 60%",
            }}
          />
        ) : null}
      </div>
      {tokens.sectionHeaderRuleWidth === "full" ? (
        <div
          style={{ height: 2, backgroundColor: accentColor, marginTop: 4 }}
        />
      ) : null}
    </div>
  );
}

function LinkedText({
  contact,
  color,
}: {
  contact: WebCvContact;
  color: string;
}) {
  if (!contact.href) {
    return (
      <span
        dir={contact.id === "location" ? "auto" : "ltr"}
        style={{
          minWidth: 0,
          overflowWrap: "anywhere",
          wordBreak: "break-word",
        }}
      >
        {contact.label}
      </span>
    );
  }

  return (
    <a
      dir="ltr"
      href={contact.href}
      target={contact.href.startsWith("http") ? "_blank" : undefined}
      rel={contact.href.startsWith("http") ? "noopener noreferrer" : undefined}
      style={{
        minWidth: 0,
        color,
        textDecoration: "none",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
      }}
    >
      {contact.label}
    </a>
  );
}

async function writeToClipboard(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textArea = document.createElement("textarea");
  textArea.value = value;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  document.body.appendChild(textArea);
  textArea.select();
  const copied = document.execCommand("copy");
  textArea.remove();
  if (!copied) throw new Error("Clipboard write failed");
}

function ContactMenu({
  label,
  channels,
  color,
  copyValue,
}: {
  label: string;
  channels: WebCvContact[];
  color: string;
  copyValue?: string;
}) {
  const [copied, setCopied] = useState(false);
  const onAccent = color.includes("255,255,255");
  if (!channels.length) return null;

  const handleCopy = async () => {
    try {
      await writeToClipboard(copyValue ?? label);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  return (
    <details
      className="tabbio-cv-contact-menu"
      style={{ position: "relative" }}
      data-ignore-section-edit
    >
      <summary
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          minHeight: 30,
          padding: "0 11px",
          border: onAccent
            ? "1px solid rgba(255,255,255,0.34)"
            : "1px solid rgba(10,10,10,0.14)",
          borderRadius: 999,
          background: onAccent ? "rgba(255,255,255,0.12)" : "#fff",
          color,
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        <CvContactIcon id={channels[0].id} color={color} />
        {label}
      </summary>
      <div
        style={{
          position: "absolute",
          insetInlineStart: 0,
          top: "calc(100% + 8px)",
          zIndex: 5,
          width: 220,
          padding: 6,
          border: "1px solid rgba(10,10,10,0.10)",
          borderRadius: 12,
          background: "#fff",
          boxShadow: "0 14px 35px rgba(10,10,10,0.14)",
        }}
      >
        {channels.map((channel) => (
          <a
            key={`${channel.id}-${channel.href}`}
            href={channel.href ?? undefined}
            target={channel.href?.startsWith("http") ? "_blank" : undefined}
            rel={
              channel.href?.startsWith("http")
                ? "noopener noreferrer"
                : undefined
            }
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              padding: "9px 10px",
              borderRadius: 8,
              color: TEXT,
              textDecoration: "none",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <CvContactIcon id={channel.id} color={TEXT} />
            {channel.label}
          </a>
        ))}
        {copyValue ? (
          <button
            type="button"
            onClick={handleCopy}
            style={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              gap: 9,
              padding: "9px 10px",
              border: 0,
              borderRadius: 8,
              background: "transparent",
              color: TEXT,
              font: "inherit",
              fontSize: 13,
              fontWeight: 600,
              textAlign: "start",
              cursor: "pointer",
            }}
          >
            <span aria-hidden="true" style={{ fontSize: 14 }}>
              ⧉
            </span>
            {copied ? "Copied" : "Copy"}
          </button>
        ) : null}
      </div>
    </details>
  );
}

function isInteractiveTarget(
  target: EventTarget | null,
  currentTarget: HTMLElement,
) {
  if (!(target instanceof Element)) {
    return false;
  }

  const interactive = target.closest(
    "a,button,input,select,textarea,[data-ignore-section-edit]",
  );
  return Boolean(interactive && currentTarget.contains(interactive));
}

function EditableRegion({
  editSection,
  onEditSection,
  label,
  style,
  className,
  children,
}: {
  editSection: WebCvEditSection;
  onEditSection?: (section: WebCvEditSection) => void;
  label: string;
  style?: CSSProperties;
  className?: string;
  children: ReactNode;
}) {
  if (!onEditSection) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    );
  }

  const triggerEdit = () => {
    onEditSection(editSection);
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (isInteractiveTarget(event.target, event.currentTarget)) {
      return;
    }
    triggerEdit();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (isInteractiveTarget(event.target, event.currentTarget)) {
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      triggerEdit();
    }
  };

  return (
    <div
      className={[EDITABLE_REGION_CLASS, className].filter(Boolean).join(" ")}
      role="button"
      tabIndex={0}
      aria-label={label || editSection}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      style={style}
    >
      {children}
    </div>
  );
}

function ProfilePhotoControl({
  src,
  alt,
  style,
  onEdit,
  editLabel,
}: {
  src: string;
  alt: string;
  style: CSSProperties;
  onEdit?: () => void;
  editLabel: string;
}) {
  const image = <img src={src} alt={alt} style={style} />;
  if (!onEdit) return image;

  return (
    <button
      type="button"
      data-ignore-section-edit
      aria-label={editLabel}
      onClick={(event) => {
        event.stopPropagation();
        onEdit();
      }}
      style={{
        appearance: "none",
        background: "transparent",
        border: 0,
        borderRadius: style.borderRadius,
        cursor: "pointer",
        flexShrink: 0,
        margin: 0,
        padding: 0,
      }}
    >
      {image}
    </button>
  );
}

export function WebCvRenderer({
  readOnlyProjects = false,
  profile: sourceProfile,
  config,
  labels: labelOverrides,
  contacts,
  topSlot,
  bodyLeadSlot,
  maxWidth = 850,
  className,
  style,
  appearance = "light",
  onEditSection,
  onEditPhoto,
  editPhotoLabel = "Edit profile photo",
  showHeader = true,
  isRTL: requestedIsRTL = false,
  sectionsConfig,
  viewerMode = "self",
  verification,
  onVerifyPress,
}: WebCvRendererProps) {
  const profile = useMemo(
    () => normalizeCvProfileContent(sourceProfile),
    [sourceProfile],
  );
  // The document renders in ITS OWN language, never the app chrome's: an
  // English CV inside the Arabic app stays fully English/LTR (labels
  // included), matching the exports. `requestedIsRTL` (the app locale) only
  // breaks the tie when the CV has no detectable narrative content yet
  // (e.g. a fresh manual build).
  const contentDirection = profile.documentDirection ?? detectCvContentDirection(profile);
  const contentIsRTL = contentDirection === "rtl";
  const isRTL = contentDirection === null ? requestedIsRTL : contentIsRTL;
  // Label overrides come from the host app's t() and follow the APP locale, so
  // drop them whenever their script disagrees with the document's resolved
  // direction: an RTL document never takes Latin-only labels, and an LTR
  // document never takes Arabic ones (the built-in label sets cover both).
  const overridesContainArabic =
    labelOverrides &&
    Object.values(labelOverrides).some((label) => containsArabicScript(label));
  const localizedOverrides = !labelOverrides
    ? undefined
    : isRTL
      ? overridesContainArabic
        ? labelOverrides
        : undefined
      : overridesContainArabic
        ? undefined
        : labelOverrides;
  const labels = {
    ...(isRTL ? AR_WEB_CV_LABELS : DEFAULT_WEB_CV_LABELS),
    ...localizedOverrides,
  };
  // SCRUM-241: text elements use `dir="auto"` so each block picks its own
  // direction from the leading strong character. Pair that with the logical
  // `start` keyword so alignment follows the resolved direction (English
  // content stays left-aligned even when isRTL is true).
  const textAlignStart: CSSProperties["textAlign"] = "start";
  const nameDir = resolveTextDir(profile.name);
  const tokens = getLayoutTokens(config.style);
  const accentColor = getAccentHex(config.color);
  const adaptBlackSectionColors =
    appearance === "adaptive" && config.color === "BLACK";
  const sectionAccentColor = adaptBlackSectionColors
    ? ADAPTIVE_BLACK_SECTION_ACCENT
    : accentColor;
  const sectionHeadingColor = adaptBlackSectionColors
    ? ADAPTIVE_BLACK_SECTION_HEADING
    : undefined;
  // Accent used as ink (text/markers/icons) and as the soft pill fill. Accent
  // used as a *fill* under white text (the CREATIVE header band) keeps the raw
  // hex: a black band still reads as a band on a dark sheet.
  const accentInkColor = adaptBlackSectionColors
    ? ADAPTIVE_BLACK_ACCENT_INK
    : accentColor;
  const bulletMarkerColor = tokens.bulletUsesAccent
    ? accentInkColor
    : TEXT_TERTIARY;
  const accentSoftColor = adaptBlackSectionColors
    ? ADAPTIVE_BLACK_ACCENT_SOFT
    : `${accentColor}18`;
  const hideTabbioLink = config.hideTabbioLink === true;
  const canonicalTabbioLink = resolveCanonicalTabbioLink(
    profile,
    viewerMode,
    hideTabbioLink,
  );
  const suppliedContacts =
    contacts ?? defaultContacts(profile, canonicalTabbioLink);
  const visibleContacts = suppliedContacts.filter(
    (contact) =>
      // A caller-supplied `contacts` list can carry its own `tabbio` entry
      // (apps/web builds one for share links), so the opt-out is applied here
      // rather than only where the canonical link is derived.
      (!hideTabbioLink || contact.id !== "tabbio") &&
      (viewerMode !== "public" ||
        (contact.id !== "phone" && contact.id !== "email")),
  );
  const channelContacts = visibleContacts.filter(
    (contact) =>
      contact.kind === "channel" ||
      ["whatsapp", "telegram", "tabbio-message"].includes(contact.id),
  );
  const phoneContact = visibleContacts.find(
    (contact) => contact.id === "phone",
  );
  const emailContact = visibleContacts.find(
    (contact) => contact.id === "email",
  );
  const locationContact = visibleContacts.find(
    (contact) => contact.id === "location",
  );
  const canonicalTabbio: WebCvContact | null =
    visibleContacts.find((contact) => contact.id === "tabbio") ??
    (canonicalTabbioLink
      ? {
          id: "tabbio" as const,
          ...canonicalTabbioLink,
          kind: "link" as const,
        }
      : null);
  const socialLinks = [
    ...(canonicalTabbio ? [canonicalTabbio] : []),
    ...visibleContacts.filter(
      (contact) =>
        ![
          "location",
          "phone",
          "email",
          "tabbio",
          "whatsapp",
          "telegram",
          "tabbio-message",
        ].includes(contact.id) &&
        contact.kind !== "fact" &&
        contact.kind !== "channel",
    ),
  ].slice(0, 5);
  const isCreative = config.style === "CREATIVE";
  const headerTextColor = isCreative
    ? "#fff"
    : tokens.nameUsesAccent
      ? accentInkColor
      : TEXT;
  const headerSubColor = isCreative ? "rgba(255,255,255,0.82)" : TEXT_SECONDARY;
  const contactIconColor = isCreative
    ? "rgba(255,255,255,0.82)"
    : tokens.contactShowIcons
      ? accentInkColor
      : TEXT_SECONDARY;
  // Font resolution: apps/web exposes the faces as next/font CSS variables
  // (hashed family names), while Expo web loads the literal families via
  // Google Fonts <link>s injected by FontProvider — so each stack tries the
  // variable first and the literal name second before system fallbacks.
  const fontFamily = isRTL
    ? "var(--font-cairo, 'Cairo'), 'Cairo', 'Noto Sans Arabic', 'Tahoma', sans-serif"
    : tokens.fontFamily === "serif"
      ? "var(--font-source-serif, 'Source Serif 4'), 'Source Serif 4', 'Georgia', 'Times New Roman', serif"
      : "var(--font-inter, 'Inter'), 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
  // Body rhythm comes from the shared tokens; Cairo needs extra leading.
  const bodyLineHeight = `${
    tokens.bodyLineHeight + (isRTL ? CV_SCREEN_RHYTHM.rtlLineHeightBoost : 0)
  }px`;
  const bodySize = tokens.bodySize;

  // Skills two-way design: `sectionsConfig.order` is an include-list: when
  // present, exactly those sections render in that order and any omitted id
  // is hidden. Absent/degenerate input falls back to the legacy hardcoded
  // order so existing CVs render unchanged. The header always renders first
  // (it is not a section), and content-empty sections still skip.
  const sectionOrder = getVisibleCvSections(sectionsConfig);

  // ── Fit to one page (config.fitToOnePage, off by default) ──
  // Mirrors the PDF export's shrink-to-fit: measure the content's natural
  // height against the one-page sheet and scale it down (transform + width
  // compensation ≙ uniformly smaller type) to the same legibility floor the
  // export uses. Desktop only — below 720px the sheet has no page metaphor.
  const fitToOnePage = config.fitToOnePage === true;
  const documentRef = useRef<HTMLDivElement | null>(null);
  const fitContentRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const contentEl = fitContentRef.current;
    const documentEl = documentRef.current;
    if (!contentEl || !documentEl || typeof window === "undefined") return;

    const reset = () => {
      contentEl.style.transform = "";
      contentEl.style.width = "";
      documentEl.style.removeProperty("--cv-fit-scale");
    };

    if (!fitToOnePage) {
      reset();
      return;
    }

    const SHEET_HEIGHT = 1202; // .tabbio-web-cv-document min-height
    const FIT_MIN_SCALE = 0.72;
    const applyFit = () => {
      reset();
      const computed = window.getComputedStyle(documentEl);
      const available =
        SHEET_HEIGHT -
        (parseFloat(computed.paddingTop) || 0) -
        (parseFloat(computed.paddingBottom) || 0);
      if (available <= 0) return;
      // Width compensation reflows the text (more per line), so converge over
      // a few passes instead of trusting the first measurement.
      let scale = 1;
      for (let pass = 0; pass < 4; pass += 1) {
        const visualHeight = contentEl.scrollHeight * scale;
        if (visualHeight <= available + 1 || scale <= FIT_MIN_SCALE) break;
        scale = Math.max(FIT_MIN_SCALE, (available / visualHeight) * scale);
        contentEl.style.width = `${100 / scale}%`;
        contentEl.style.transform = `scale(${scale})`;
        documentEl.style.setProperty("--cv-fit-scale", String(scale));
      }
    };

    const mq = window.matchMedia("(min-width: 720px)");
    const run = () => {
      if (mq.matches) applyFit();
      else reset();
    };
    run();
    // Web fonts swapping in after first paint changes line counts.
    if (typeof document !== "undefined" && document.fonts?.ready) {
      void document.fonts.ready.then(run);
    }
    mq.addEventListener("change", run);
    return () => mq.removeEventListener("change", run);
  }, [fitToOnePage, profile, config, sectionsConfig, viewerMode, isRTL]);

  const renderSection = (sectionId: CvSectionId): ReactNode => {
    switch (sectionId) {
      case "summary":
        return profile.summary ? (
          <EditableRegion
            editSection="edit-summary"
            onEditSection={onEditSection}
            label={labels.profileSummary}
          >
            <SectionHeader
              title={labels.profileSummary}
              accentColor={sectionAccentColor}
              headingColor={sectionHeadingColor}
              tokens={tokens}
            />
            <p
              dir="auto"
              style={{
                fontSize: bodySize,
                lineHeight: bodyLineHeight,
                color: TEXT,
                margin: 0,
                fontFamily,
                textAlign: textAlignStart,
              }}
            >
              {profile.summary}
            </p>
          </EditableRegion>
        ) : null;

      case "careerHighlights":
        return profile.highlights.length > 0 ? (
          <EditableRegion
            editSection="edit-highlights"
            onEditSection={onEditSection}
            label={labels.careerHighlights}
          >
            <SectionHeader
              title={labels.careerHighlights}
              accentColor={sectionAccentColor}
              headingColor={sectionHeadingColor}
              tokens={tokens}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: CV_SCREEN_RHYTHM.highlightGap,
              }}
            >
              {profile.highlights.map((highlight, index) => (
                <p
                  key={highlight.id ?? `${highlight.title}-${index}`}
                  dir="auto"
                  style={{
                    fontSize: bodySize,
                    lineHeight: bodyLineHeight,
                    color: TEXT,
                    margin: 0,
                    fontFamily,
                    textAlign: textAlignStart,
                  }}
                >
                  {/* Colon + bold label only when the title labels real text;
                      a one-part highlight is body text, not a label. */}
                  {highlight.title?.trim() && highlight.desc?.trim() ? (
                    <>
                      <strong>{highlight.title}:</strong> {highlight.desc}
                    </>
                  ) : (
                    highlight.title?.trim() || highlight.desc
                  )}
                </p>
              ))}
            </div>
          </EditableRegion>
        ) : null;

      case "skills":
        return profile.skillGroups.length > 0 ? (
          <EditableRegion
            editSection="edit-skills"
            onEditSection={onEditSection}
            label={labels.keySkills}
          >
            <SectionHeader
              title={labels.keySkills}
              accentColor={sectionAccentColor}
              headingColor={sectionHeadingColor}
              tokens={tokens}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: CV_SCREEN_RHYTHM.skillGroupGap,
              }}
            >
              {profile.skillGroups.map((group, groupIndex) => (
                <div
                  key={`${group.category}-${groupIndex}`}
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: 0,
                      textTransform: "uppercase",
                      color: TEXT_TERTIARY,
                    }}
                  >
                    {group.category}
                  </span>
                  {tokens.skillsAsPills ? (
                    // `dir="auto"` so the pill row flows from the same edge its
                    // own text reads from: English pills stay LTR even inside an
                    // RTL document, matching the `dir="auto"` text blocks above.
                    <div
                      dir="auto"
                      style={{ display: "flex", flexWrap: "wrap", gap: 6 }}
                    >
                      {group.items.map((skill, skillIndex) => (
                        <span
                          key={`${skill}-${skillIndex}`}
                          dir={resolveBidiTextDirection(
                            skill,
                            isRTL ? "rtl" : "ltr",
                          )}
                          style={{
                            display: "inline-block",
                            padding: "4px 12px",
                            borderRadius: tokens.skillPillRadius,
                            fontSize: 13,
                            fontWeight: 500,
                            fontFamily,
                            backgroundColor: tokens.skillPillUsesAccent
                              ? accentSoftColor
                              : BORDER,
                            color: tokens.skillPillUsesAccent
                              ? accentInkColor
                              : TEXT,
                          }}
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <BidiInlineList
                      values={group.items}
                      separator=","
                      baseDirection={isRTL ? "rtl" : "ltr"}
                      style={{
                        fontSize: bodySize,
                        lineHeight: bodyLineHeight,
                        color: TEXT,
                        margin: 0,
                        fontFamily,
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </EditableRegion>
        ) : null;

      case "experience":
        return profile.experience.length > 0 ? (
          <EditableRegion
            editSection="edit-experience"
            onEditSection={onEditSection}
            label={labels.professionalExperience}
          >
            <SectionHeader
              title={labels.professionalExperience}
              accentColor={sectionAccentColor}
              headingColor={sectionHeadingColor}
              tokens={tokens}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: tokens.itemGap,
              }}
            >
              {profile.experience.map((experience) => (
                <div
                  key={experience.id}
                  style={{ display: "flex", flexDirection: "column", gap: 2 }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 8,
                    }}
                  >
                    <span
                      dir="auto"
                      style={{
                        flex: 1,
                        fontSize: 14,
                        fontWeight: 600,
                        color: TEXT,
                        fontFamily,
                        textAlign: textAlignStart,
                      }}
                    >
                      {experience.role}
                    </span>
                    {experience.period ? (
                      <span
                        dir="ltr"
                        style={{
                          fontSize: 12,
                          color: TEXT_TERTIARY,
                          flexShrink: 0,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {experience.period}
                      </span>
                    ) : null}
                  </div>
                  <BidiInlineList
                    values={[
                      experience.company,
                      experience.location,
                      experience.locationType,
                      experience.employmentType,
                    ]}
                    separator="|"
                    baseDirection={isRTL ? "rtl" : "ltr"}
                    style={{
                      fontSize: 13,
                      color: TEXT_SECONDARY,
                      fontFamily,
                      textAlign: textAlignStart,
                      ...(tokens.overviewItalic ? { fontStyle: "italic" } : {}),
                    }}
                  />
                  {experience.overview ? (
                    <p
                      dir="auto"
                      style={{
                        fontSize: bodySize,
                        lineHeight: bodyLineHeight,
                        color: TEXT_SECONDARY,
                        margin: `${CV_SCREEN_RHYTHM.overviewGapTop}px 0 0`,
                        fontFamily,
                        textAlign: textAlignStart,
                        ...(tokens.overviewItalic
                          ? { fontStyle: "italic" }
                          : {}),
                      }}
                    >
                      {experience.overview}
                    </p>
                  ) : null}
                  {experience.bullets.length > 0 ? (
                    <ul
                      style={{
                        margin: `${CV_SCREEN_RHYTHM.bulletListGapTop}px 0 0`,
                        padding: 0,
                        listStyle: "none",
                        display: "flex",
                        flexDirection: "column",
                        gap: CV_SCREEN_RHYTHM.bulletGap,
                      }}
                    >
                      {experience.bullets.map((bullet, bulletIndex) => (
                        // Explicit markers survive CSS resets and keep the web
                        // preview aligned with native and exported documents.
                        // dir=auto ignores descendants with their own dir
                        // attribute, so resolve from the bullet text explicitly.
                        <li
                          key={`${bullet}-${bulletIndex}`}
                          dir={resolveBidiTextDirection(
                            bullet,
                            isRTL ? "rtl" : "ltr",
                          )}
                          style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "flex-start",
                          }}
                        >
                          <span
                            aria-hidden="true"
                            style={{
                              width: 8,
                              flexShrink: 0,
                              color: bulletMarkerColor,
                              fontSize: 15,
                              lineHeight: bodyLineHeight,
                              textAlign: "center",
                            }}
                          >
                            •
                          </span>
                          <span
                            dir="auto"
                            style={{
                              flex: 1,
                              fontSize: bodySize,
                              lineHeight: bodyLineHeight,
                              color: TEXT,
                              fontFamily,
                              textAlign: textAlignStart,
                            }}
                          >
                            {bullet}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ))}
            </div>
          </EditableRegion>
        ) : null;

      case "projects":
        return profile.projects?.length ? (
          <EditableRegion
            editSection="edit-projects"
            onEditSection={readOnlyProjects ? undefined : onEditSection}
            label={labels.projects}
          >
            <SectionHeader
              title={labels.projects}
              accentColor={sectionAccentColor}
              headingColor={sectionHeadingColor}
              tokens={tokens}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: tokens.itemGap,
              }}
            >
              {profile.projects.map((project) => (
                <div
                  key={project.id}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    minWidth: 0,
                  }}
                >
                  <strong
                    dir="auto"
                    style={{ fontSize: 14, color: TEXT, fontFamily }}
                  >
                    {project.title}
                  </strong>
                  {project.description ? (
                    <p
                      dir="auto"
                      style={{
                        margin: 0,
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                        fontSize: bodySize,
                        lineHeight: bodyLineHeight,
                        color: TEXT,
                        fontFamily,
                      }}
                    >
                      {project.description}
                    </p>
                  ) : null}
                  <BidiInlineList
                    values={project.technologies}
                    separator=","
                    baseDirection={isRTL ? "rtl" : "ltr"}
                    style={{
                      fontSize: bodySize,
                      lineHeight: bodyLineHeight,
                      color: TEXT_SECONDARY,
                      fontFamily,
                    }}
                  />
                  {project.link ? (
                    <span
                      dir="ltr"
                      style={{
                        fontSize: bodySize,
                        lineHeight: bodyLineHeight,
                        overflowWrap: "anywhere",
                        color: TEXT_SECONDARY,
                      }}
                    >
                      {project.link}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          </EditableRegion>
        ) : null;

      case "education":
        return profile.education.length > 0 ? (
          <EditableRegion
            editSection="edit-education"
            onEditSection={onEditSection}
            label={labels.education}
          >
            <SectionHeader
              title={labels.education}
              accentColor={sectionAccentColor}
              headingColor={sectionHeadingColor}
              tokens={tokens}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: CV_SCREEN_RHYTHM.educationGap,
              }}
            >
              {profile.education.map((education) => {
                const detail = splitEducationDescription(education.description);
                return (
                  <div
                    key={education.id}
                    style={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 8,
                      }}
                    >
                      <span
                        dir="auto"
                        style={{
                          flex: 1,
                          fontSize: 14,
                          fontWeight: 600,
                          color: TEXT,
                          fontFamily,
                          textAlign: textAlignStart,
                        }}
                      >
                        {education.degree}
                      </span>
                      {education.year ? (
                        <span
                          dir="ltr"
                          style={{
                            fontSize: 12,
                            color: TEXT_TERTIARY,
                            flexShrink: 0,
                          }}
                        >
                          {education.year}
                        </span>
                      ) : null}
                    </div>
                    <span
                      dir="auto"
                      style={{
                        fontSize: 13,
                        color: TEXT_SECONDARY,
                        fontFamily,
                        textAlign: textAlignStart,
                      }}
                    >
                      {education.school}
                    </span>
                    {detail.narrative ? (
                      <p
                        dir="auto"
                        style={{
                          fontSize: bodySize,
                          lineHeight: bodyLineHeight,
                          color: TEXT_SECONDARY,
                          margin: "4px 0 0",
                          fontFamily,
                          textAlign: textAlignStart,
                        }}
                      >
                        {detail.narrative}
                      </p>
                    ) : null}
                    {detail.bullets.length ? (
                      <ul
                        dir="auto"
                        style={{
                          margin: `${CV_SCREEN_RHYTHM.bulletListGapTop}px 0 0`,
                          paddingInlineStart: 16,
                          display: "flex",
                          flexDirection: "column",
                          gap: CV_SCREEN_RHYTHM.bulletGap,
                        }}
                      >
                        {detail.bullets.map((bullet, bulletIndex) => (
                          <li
                            key={`${bullet}-${bulletIndex}`}
                            dir="auto"
                            style={{
                              fontSize: bodySize,
                              lineHeight: bodyLineHeight,
                              color: TEXT_SECONDARY,
                              fontFamily,
                              textAlign: textAlignStart,
                            }}
                          >
                            {bullet}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </EditableRegion>
        ) : null;

      case "certifications":
        return profile.certifications.length > 0 ? (
          <EditableRegion
            editSection="edit-certifications"
            onEditSection={onEditSection}
            label={labels.certifications}
          >
            <SectionHeader
              title={labels.certifications}
              accentColor={sectionAccentColor}
              headingColor={sectionHeadingColor}
              tokens={tokens}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: CV_SCREEN_RHYTHM.certificationGap,
              }}
            >
              {profile.certifications.map((certification) => (
                <div
                  key={certification.id}
                  style={{ display: "flex", flexDirection: "column", gap: 2 }}
                >
                  {certification.name ? (
                    <span
                      dir="auto"
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: TEXT,
                        fontFamily,
                        textAlign: textAlignStart,
                      }}
                    >
                      {certification.name}
                    </span>
                  ) : null}
                  <span
                    dir="auto"
                    style={{
                      fontSize: 13,
                      color: TEXT_SECONDARY,
                      fontFamily,
                      textAlign: textAlignStart,
                      display: "inline-flex",
                      minWidth: 0,
                      maxWidth: "100%",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <BidiInlineList
                      values={[certification.issuer, certification.date]}
                      separator="·"
                      baseDirection={isRTL ? "rtl" : "ltr"}
                    />
                    {certification.credentialUrl ? (
                      <a
                        href={certification.credentialUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={labels.verifyCredential}
                        title={labels.verifyCredential}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          color: accentInkColor,
                          flexShrink: 0,
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M15 3h6v6" />
                          <path d="M10 14 21 3" />
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6" />
                        </svg>
                      </a>
                    ) : null}
                  </span>
                </div>
              ))}
            </div>
          </EditableRegion>
        ) : null;

      case "languages":
        return profile.languages.length > 0 ? (
          <EditableRegion
            editSection="edit-languages"
            onEditSection={onEditSection}
            label={labels.languages}
          >
            <SectionHeader
              title={labels.languages}
              accentColor={sectionAccentColor}
              headingColor={sectionHeadingColor}
              tokens={tokens}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: CV_SCREEN_RHYTHM.languageGap,
              }}
            >
              {profile.languages.map((entry, index) => {
                const localizedEntry = localizeWebCvLanguage(entry, isRTL);
                // Each entry is stored as `Language (Proficiency)`: split it so the
                // language reads as the primary term and the proficiency as a muted
                // qualifier, instead of cramming everything into one ` · ` line.
                const match = localizedEntry.match(/^(.+?)\s*\((.+)\)\s*$/);
                const name = match ? match[1].trim() : localizedEntry;
                const proficiency = match ? match[2].trim() : "";
                return (
                  <p
                    key={`${entry}-${index}`}
                    dir="auto"
                    style={{
                      fontSize: bodySize,
                      lineHeight: bodyLineHeight,
                      color: TEXT,
                      margin: 0,
                      fontFamily,
                      textAlign: textAlignStart,
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{name}</span>
                    {proficiency ? (
                      <span
                        style={{ fontWeight: 500, color: TEXT_SECONDARY }}
                      >{` · ${proficiency}`}</span>
                    ) : null}
                  </p>
                );
              })}
            </div>
          </EditableRegion>
        ) : null;
    }
  };

  return (
    <div
      className={SHELL_CLASS}
      dir={isRTL ? "rtl" : "ltr"}
      style={{ maxWidth }}
    >
      <style>{DOCUMENT_STYLES}</style>
      {onEditSection ? <style>{EDITABLE_REGION_STYLES}</style> : null}
      {topSlot}

      <div
        ref={documentRef}
        className={[
          DOCUMENT_CLASS,
          `${STYLE_CLASS_PREFIX}${config.style}`,
          appearance === "adaptive" ? ADAPTIVE_DOCUMENT_CLASS : null,
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        style={{
          width: "100%",
          color: TEXT,
          fontFamily,
          ...style,
        }}
      >
        <div
          ref={fitContentRef}
          // Fit-to-one-page target: transform/width are managed imperatively
          // by the measurement effect above so React never fights the values.
          style={{ transformOrigin: "top left" }}
        >
          {showHeader ? (
            <EditableRegion
              editSection="edit-header"
              onEditSection={onEditSection}
              label={profile.name}
              className={isCreative ? CREATIVE_HEADER_CLASS : undefined}
              style={
                isCreative
                  ? {
                      backgroundColor: accentColor,
                    }
                  : undefined
              }
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: CV_SCREEN_RHYTHM.headerPhotoGap,
                  flexDirection: isRTL ? "row-reverse" : "row",
                }}
              >
                {config.showPhoto && profile.avatarUrl ? (
                  <ProfilePhotoControl
                    src={profile.avatarUrl}
                    alt={profile.name}
                    onEdit={onEditPhoto}
                    editLabel={editPhotoLabel}
                    style={{
                      width: CV_SCREEN_RHYTHM.photoSize,
                      height: CV_SCREEN_RHYTHM.photoSize,
                      borderRadius: tokens.photoShape === "square" ? 4 : "50%",
                      objectFit: "cover",
                      ...(tokens.photoBorder
                        ? { border: `1px solid ${BORDER}` }
                        : {}),
                      flexShrink: 0,
                    }}
                  />
                ) : null}

                <div style={{ flex: 1, minWidth: 0 }}>
                  {/*
                  The name is a shrink-to-fit flex item, so in an RTL document
                  the row parked an English name against the right edge even
                  though the <h1> itself resolved to LTR (its `text-align: start`
                  had no slack to work with). Give the row the name's own
                  direction so it starts from the edge the name reads from; an
                  Arabic name still resolves to rtl and is unaffected.
                */}
                  <div dir={nameDir}>
                    <h1
                      dir="auto"
                      style={{
                        fontSize: tokens.nameSize,
                        fontWeight: tokens.nameWeight,
                        color: headerTextColor,
                        margin: 0,
                        letterSpacing: 0,
                        fontFamily,
                        textAlign: textAlignStart,
                        ...(tokens.nameUppercase
                          ? { textTransform: "uppercase" }
                          : {}),
                      }}
                    >
                      {profile.name}
                      {/*
                      Inside the <h1>, not a flex sibling of it: as a sibling the
                      mark was laid out against the row's far edge and centered
                      vertically, so the moment the name wrapped (narrow
                      viewports, long names) it detached and floated off to the
                      right of the header. In the text flow it follows the last
                      word and wraps with it.
                    */}
                      <UaePassVerifiedControl
                        verification={verification}
                        color={headerTextColor}
                        onVerifyPress={onVerifyPress}
                      />
                    </h1>
                  </div>
                  {profile.headline ? (
                    <p
                      dir="auto"
                      style={{
                        fontSize: 15,
                        color: headerSubColor,
                        margin: "3px 0 0",
                        lineHeight: "20px",
                        fontFamily,
                        textAlign: textAlignStart,
                      }}
                    >
                      {profile.headline}
                    </p>
                  ) : null}
                </div>
              </div>

              {locationContact ||
              phoneContact ||
              emailContact ||
              channelContacts.length ? (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 12,
                    marginTop: CV_SCREEN_RHYTHM.headerContactGapTop,
                    alignItems: "center",
                  }}
                >
                  {locationContact ? (
                    <span
                      style={{
                        display: "inline-flex",
                        minWidth: 0,
                        maxWidth: "100%",
                        alignItems: "center",
                        gap: 5,
                        fontSize: 12,
                        color: headerSubColor,
                      }}
                    >
                      {tokens.contactShowIcons ? (
                        <CvContactIcon id="location" color={contactIconColor} />
                      ) : null}
                      <LinkedText
                        contact={locationContact}
                        color={headerSubColor}
                      />
                    </span>
                  ) : null}
                  {viewerMode === "public" && channelContacts.length ? (
                    <ContactMenu
                      label="Contact"
                      channels={channelContacts}
                      color={headerSubColor}
                    />
                  ) : null}
                  {viewerMode !== "public" && phoneContact ? (
                    channelContacts.some(
                      (contact) => contact.id === "whatsapp",
                    ) ? (
                      <ContactMenu
                        label={phoneContact.label}
                        copyValue={phoneContact.label}
                        color={headerSubColor}
                        channels={[
                          ...channelContacts.filter(
                            (contact) => contact.id === "whatsapp",
                          ),
                          { ...phoneContact, label: "Call", kind: "channel" },
                        ]}
                      />
                    ) : (
                      <span
                        style={{
                          display: "inline-flex",
                          minWidth: 0,
                          maxWidth: "100%",
                          alignItems: "center",
                          gap: 5,
                          fontSize: 12,
                          color: headerSubColor,
                        }}
                      >
                        {tokens.contactShowIcons ? (
                          <CvContactIcon id="phone" color={contactIconColor} />
                        ) : null}
                        <LinkedText
                          contact={phoneContact}
                          color={headerSubColor}
                        />
                      </span>
                    )
                  ) : null}
                  {viewerMode !== "public" && emailContact ? (
                    <span
                      style={{
                        display: "inline-flex",
                        minWidth: 0,
                        maxWidth: "100%",
                        alignItems: "center",
                        gap: 5,
                        fontSize: 12,
                        color: headerSubColor,
                      }}
                    >
                      {tokens.contactShowIcons ? (
                        <CvContactIcon id="email" color={contactIconColor} />
                      ) : null}
                      <LinkedText
                        contact={emailContact}
                        color={headerSubColor}
                      />
                    </span>
                  ) : null}
                  {viewerMode !== "public"
                    ? channelContacts
                        .filter((contact) => contact.id !== "whatsapp")
                        .map((contact) => (
                          <span
                            key={`${contact.id}-${contact.label}`}
                            style={{
                              display: "inline-flex",
                              minWidth: 0,
                              maxWidth: "100%",
                              alignItems: "center",
                              gap: 5,
                              fontSize: 12,
                              color: headerSubColor,
                            }}
                          >
                            <CvContactIcon
                              id={contact.id}
                              color={contactIconColor}
                            />
                            <LinkedText
                              contact={contact}
                              color={headerSubColor}
                            />
                          </span>
                        ))
                    : null}
                </div>
              ) : null}

              {socialLinks.length ? (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 12,
                    marginTop: CV_SCREEN_RHYTHM.headerSocialGapTop,
                    alignItems: "center",
                  }}
                >
                  {socialLinks.map((contact) => (
                    <span
                      key={`${contact.id}-${contact.label}`}
                      style={{
                        display: "inline-flex",
                        minWidth: 0,
                        maxWidth: "100%",
                        alignItems: "center",
                        gap: 5,
                        fontSize: 12,
                        color: headerSubColor,
                      }}
                    >
                      {/* A social link's brand mark is part of the link itself,
                        so every theme renders it; `contactShowIcons` only
                        gates the utility icons (phone/email) above. */}
                      <CvContactIcon id={contact.id} color={contactIconColor} />
                      <LinkedText contact={contact} color={headerSubColor} />
                    </span>
                  ))}
                </div>
              ) : null}
            </EditableRegion>
          ) : null}

          {bodyLeadSlot}

          {sectionOrder.map((sectionId) => (
            <Fragment key={sectionId}>{renderSection(sectionId)}</Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
